import { Injectable, Inject } from '@angular/core'
import { DOCUMENT } from '@angular/common'
import { BehaviorSubject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { WhiteLabelService, WhiteLabelTheme } from '../route/account/white-label-settings/white-label.service'
import { TenantService } from './tenant-service'
import { ConsumerViewService } from './consumer-view-service'
import { consumerViewStorageKeys } from '../constant'

export interface BrandLogos {
    rectangle: string
    square: string
    fav: string
}

/**
 * Service responsible for managing white-label theming and tenant branding.
 *
 * IMPORTANT: This service handles platform white-labeling (e.g., flok-pro.showcase.reservests.com)
 * and should not be confused with hotel chain brands (e.g., Marriott, Hilton) which are domain concepts.
 *
 * The service follows a hierarchy for branding decisions:
 * 1. White-label configuration (if enabled via subdomain)
 * 2. Default tenant branding as fallback
 *
 * Use this service for:
 * - Logo management
 * - Brand name display
 * - Theme colors
 * - Typography
 * - Other white-label customizations
 *
 * @example
 * // Get appropriate logos based on white-label status
 * const logos = brandingService.getLogos();
 *
 * // Get brand name for display
 * const name = brandingService.getBrandName();
 */
@Injectable({
    providedIn: 'root'
})
export class BrandingService {
    private _currentLogos = new BehaviorSubject<BrandLogos | null>(null);
    readonly logos$ = this._currentLogos.asObservable();
    private destroy$ = new BehaviorSubject<void>(undefined);

    constructor(
        @Inject(DOCUMENT) private _document: Document,
        private _whiteLabelService: WhiteLabelService,
        private _tenantService: TenantService,
        private _consumerViewService: ConsumerViewService
    ) {
        // Don't set any logos initially if white label is enabled
        if (!this._whiteLabelService.isWhiteLabelEnabled()) {
            console.log('BrandingService: White label disabled, using tenant logos');
            this._currentLogos.next(this._tenantService.logos());
        } else {
            console.log('BrandingService: White label enabled, loading config...');
            // Immediately load white label config for showcase domains
            this._whiteLabelService.loadWhiteLabelBySubdomain()
                .subscribe({
                    next: (config) => {
                        if (config) {
                            console.log('BrandingService: Loaded initial white label config:', config);
                            const logos = {
                                rectangle: config.landscapeLogoUrl || '',
                                square: config.squareLogoUrl || '',
                                fav: config.faviconUrl || ''
                            };
                            this._currentLogos.next(logos);
                        }
                    },
                    error: (error) => {
                        console.error('BrandingService: Failed to load white label config:', error);
                    }
                });
        }

        // Subscribe to future white label config changes
        if (this._whiteLabelService.isWhiteLabelEnabled()) {
            this._whiteLabelService.whiteLabelConfig$
                .pipe(takeUntil(this.destroy$))
                .subscribe(config => {
                    if (config) {
                        console.log('BrandingService: Updating logos from white label config');
                        const logos = {
                            rectangle: config.landscapeLogoUrl || '',
                            square: config.squareLogoUrl || '',
                            fav: config.faviconUrl || ''
                        };
                        this._currentLogos.next(logos);
                    }
                });
        }
    }

    getLogos(): BrandLogos | null {
        // Check consumer view first
        if (this._consumerViewService.consumerView) {
            const consumerLogo = sessionStorage.getItem(consumerViewStorageKeys.companyLogo)
            if (consumerLogo) {
                console.log('BrandingService: Using consumer view logo');
                return {
                    rectangle: consumerLogo,
                    square: consumerLogo,
                    fav: consumerLogo
                };
            }
        }
        
        // If white label is enabled, return current logos (may be null during loading)
        if (this._whiteLabelService.isWhiteLabelEnabled()) {
            const currentLogos = this._currentLogos.getValue();
            console.log('BrandingService: White label enabled, returning current logos:', currentLogos);
            return currentLogos;
        }
        
        // Fall back to tenant logos only if white label is disabled
        console.log('BrandingService: White label disabled, using tenant logos');
        const tenantLogos = this._tenantService.logos();
        this._currentLogos.next(tenantLogos);
        return tenantLogos;
    }

    /**
     * Returns the header background color.
     * Uses white label header color if enabled, falls back to white.
     * @returns {string} CSS color value for header
     */
    getHeaderColor(): string {
        if (this._whiteLabelService.isWhiteLabelEnabled()) {
            const theme = this._whiteLabelService.getTheme();
            return theme?.headerColor || '#FFFFFF'; // Fallback to white
        }
        return '#FFFFFF'; // Default white for tenant
    }

    /**
     * Converts a hex color string to RGB values
     */
    private hexToRgb(hex: string): { r: number; g: number; b: number } {
        // Remove # if present
        hex = hex.replace('#', '')
        
        // Parse hex values
        const r = parseInt(hex.substring(0, 2), 16)
        const g = parseInt(hex.substring(2, 4), 16)
        const b = parseInt(hex.substring(4, 6), 16)
        
        return { r, g, b }
    }

    /**
     * Generates CSS custom properties for the white label theme
     */
    private generateThemeStyles(theme: WhiteLabelTheme): string {
        const primary1 = this.hexToRgb(theme.primary1);
        const primary2 = this.hexToRgb(theme.primary2);
        const primary3 = this.hexToRgb(theme.primary3);
        const primary4 = this.hexToRgb(theme.primary4);
        const green = this.hexToRgb(theme.primaryGreen);
        
        return `
            :root {
                --color-primary-1: ${primary1.r}, ${primary1.g}, ${primary1.b};
                --color-primary-2: ${primary2.r}, ${primary2.g}, ${primary2.b};
                --color-primary-3: ${primary3.r}, ${primary3.g}, ${primary3.b};
                --color-primary-4: ${primary4.r}, ${primary4.g}, ${primary4.b};
                --color-header: ${theme.headerColor};
                --color-green: ${green.r}, ${green.g}, ${green.b};
                
                /* Font families */
                --font-family-heading: ${theme.fontFamily.heading};
                --font-family-body: ${theme.fontFamily.body};
                
                /* Font Awesome */
                --fa-font-solid: normal 900 1em/1 "Font Awesome 6 Free";
                --fa-font-regular: normal 400 1em/1 "Font Awesome 6 Free";
                --fa-font-brands: normal 400 1em/1 "Font Awesome 6 Brands";
            }
        `;
    }

    /**
     * Applies the appropriate theme based on white label status
     */
    public applyWhiteLabelTheme(): void {
        if (this._whiteLabelService.isWhiteLabelEnabled()) {
            console.log('White label is enabled, loading config...');
            // First load the config, then apply theme when ready
            this._whiteLabelService.loadWhiteLabelBySubdomain()
                .subscribe({
                    next: (config) => {
                        console.log('Loaded white label config:', config);
                        if (config) {
                            const theme = this._whiteLabelService.getTheme();
                            console.log('Generated theme:', theme);
                            if (theme) {
                                const styleElement = this._document.createElement('style');
                                styleElement.innerHTML = this.generateThemeStyles(theme);
                                this._document.head.appendChild(styleElement);
                            }
                        }
                    },
                    error: (error) => {
                        console.error('Error loading white label config:', error);
                        this.applyTenantTheme();
                    }
                });
        } else {
            this.applyTenantTheme();
        }
    }

    private applyTenantTheme(): void {
        // Load Adobe Typekit for tenant
        const typekitScript = this._document.createElement('link');
        typekitScript.href = "https://use.typekit.net/lkm0fbj.css";
        typekitScript.rel = "stylesheet";
        this._document.head.appendChild(typekitScript);

        const styleElement = this._document.createElement('style');
        styleElement.innerHTML = `
            :root {
                --font-family-heading: bicyclette, sans-serif;
                --font-family-body: akzidenz-grotesk-pro, sans-serif;
                
                /* Font Awesome */
                --fa-font-solid: normal 900 1em/1 "Font Awesome 6 Free";
                --fa-font-regular: normal 400 1em/1 "Font Awesome 6 Free";
                --fa-font-brands: normal 400 1em/1 "Font Awesome 6 Brands";
            }
        `;
        this._document.head.appendChild(styleElement);
    }
}